import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type {
  LocalAlertConfig,
  LocalAlertCreateProps,
} from '../../types/localeAlerts.ts';
import { createLocalAlert } from '../../utils/alerts.tsx';

type LocalAlertsContextType = {
  alerts: LocalAlertConfig[];
  addAlerts: (alerts: LocalAlertCreateProps | LocalAlertCreateProps[]) => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
};

const LocalAlertsContext = createContext<LocalAlertsContextType | null>(null);

/**
 * Provider used for adding/displaying alerts within a given organism or page experience.
 */
export const LocalAlertsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [alerts, setAlerts] = useState<LocalAlertConfig[]>([]);

  const addAlerts = useCallback(
    (newAlerts: LocalAlertCreateProps | LocalAlertCreateProps[]) => {
      setAlerts((prevAlerts) => {
        const alertsToAdd = Array.isArray(newAlerts)
          ? newAlerts.map(createLocalAlert)
          : [createLocalAlert(newAlerts)];
        return [...prevAlerts, ...alertsToAdd];
      });
    },
    [],
  );

  const removeAlert = useCallback((id: string) => {
    setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // TODO: maybe we have a timer to close out some alerts?
  const contextValue = useMemo(() => {
    return {
      addAlerts,
      removeAlert,
      clearAlerts,
      alerts,
    };
    // eslint-disable-next-line react-compiler/react-compiler
  }, [JSON.stringify(alerts)]);
  return (
    <LocalAlertsContext.Provider value={contextValue}>
      {children}
    </LocalAlertsContext.Provider>
  );
};

/**
 * Local alerts context.
 */
export const useLocalAlerts = () => {
  const context = useContext(LocalAlertsContext);
  if (!context) {
    throw new Error(
      'useLocalAlerts must be used within <LocalAlertsProvider />',
    );
  }
  return context;
};
